/**
 * Sticky marketing header for the rotahq.uk landing page.
 * Anchors smooth-scroll to page sections; auth CTAs route into the app.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { marketingEvent } from '@/lib/marketing';

const NAV = [
  { href: '#features', label: 'Features', testid: 'marketing-nav-features-link' },
  { href: '#how-it-works', label: 'How It Works', testid: 'marketing-nav-how-it-works-link' },
  { href: '#pricing', label: 'Pricing', testid: 'marketing-nav-pricing-link' },
  { href: '#faq', label: 'FAQ', testid: 'marketing-nav-faq-link' },
];

export function BrandMark({ size = 'md' }) {
  const box = size === 'sm' ? 'h-8 w-8 rounded-lg' : 'h-9 w-9 rounded-xl';
  return (
    <span className="flex items-center gap-2.5">
      <span className={`flex ${box} items-center justify-center bg-[color:var(--brand-primary)] text-white shadow-[0_4px_12px_rgba(200,74,47,0.35)]`}>
        <Clock className="h-4.5 w-4.5 h-5 w-5" aria-hidden="true" />
      </span>
      <span className="font-display text-lg font-semibold leading-none">RotaHQ</span>
    </span>
  );
}

export function MarketingHeader({ onBookDemo }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (href) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[color:var(--brand-border)] bg-[rgba(251,246,238,0.92)] supports-[backdrop-filter]:backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <a href="#top" onClick={scrollTo('#top')} aria-label="RotaHQ — back to top" data-testid="marketing-header-logo">
          <BrandMark />
        </a>

        <nav className="hidden md:flex items-center gap-1" aria-label="Main">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={scrollTo(item.href)}
              className="px-3 py-2 rounded-lg text-sm font-medium text-[color:var(--brand-text)] hover:bg-[rgba(31,35,40,0.05)] transition-colors duration-150 focus-ring"
              data-testid={item.testid}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" className="hidden sm:inline-flex h-10 rounded-xl font-semibold hover:bg-[rgba(31,35,40,0.05)]">
            <Link to="/login" data-testid="marketing-header-login-button">Log In</Link>
          </Button>
          <Button
            asChild
            className="h-10 rounded-xl px-4 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white font-semibold transition-colors duration-150"
          >
            <Link
              to="/signup"
              onClick={() => marketingEvent('click_start_trial', { placement: 'header' })}
              data-testid="marketing-header-start-trial-button"
            >
              Start Free Trial
            </Link>
          </Button>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden h-11 w-11 rounded-xl" aria-label="Open menu" data-testid="marketing-header-mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-[color:var(--brand-bg)]">
              <div className="mt-6 flex flex-col gap-1" data-testid="marketing-mobile-menu">
                {NAV.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={scrollTo(item.href)}
                    className="px-3 py-3 rounded-xl text-base font-medium hover:bg-[rgba(31,35,40,0.05)] transition-colors duration-150"
                    data-testid={`${item.testid}-mobile`}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="border-t border-[color:var(--brand-border)] my-3" />
                <Button asChild variant="outline" className="h-12 rounded-xl font-semibold">
                  <Link to="/login" data-testid="marketing-mobile-login-button">Log In</Link>
                </Button>
                <Button asChild className="h-12 rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white font-semibold mt-2">
                  <Link
                    to="/signup"
                    onClick={() => marketingEvent('click_start_trial', { placement: 'mobile_menu' })}
                    data-testid="marketing-mobile-start-trial-button"
                  >
                    Start Free Trial
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  className="h-12 rounded-xl font-semibold mt-1"
                  onClick={() => { setMenuOpen(false); onBookDemo('mobile_menu'); }}
                  data-testid="marketing-mobile-book-demo-button"
                >
                  Book a Demo
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
